
import { defineComponent, computed, PropType } from 'vue';
import moment from 'moment-timezone';
import { formatDate } from './formatters';
import type { DateFormatType } from './formatters';
import type { DateDisplaySize } from './displayTypes';

export default defineComponent({
  name: 'SpmDateDisplay',

  props: {
    /**
     * The date value to display (string, Date, or timestamp)
     */
    value: {
      type: [String, Date, Number] as PropType<string | Date | number | null | undefined>,
      default: null,
    },

    /**
     * Format type for the date display
     */
    format: {
      type: String as PropType<DateFormatType>,
      default: 'date',
    },

    /**
     * Icon to display before the date
     */
    icon: {
      type: String,
      default: '',
    },

    /**
     * Size of the date display
     */
    size: {
      type: String as PropType<DateDisplaySize>,
      default: 'md',
    },

    /**
     * Locale for date formatting
     */
    locale: {
      type: String,
      default: 'fr-FR',
    },

    /**
     * Placeholder text when date is empty
     */
    placeholder: {
      type: String,
      default: '-',
    },

    /**
     * Timezone for date conversion (e.g., 'Europe/Paris')
     * If provided, the date will be converted from UTC to this timezone
     */
    timezone: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const formattedDate = computed(() => {
      if (!props.value) return props.placeholder;

      if (props.timezone) {
        const utcMoment = moment.utc(props.value);
        const localMoment = utcMoment.tz(props.timezone);

        switch (props.format) {
          case 'date':
            return localMoment.format('DD/MM/YYYY');
          case 'datetime':
            return localMoment.format('DD/MM/YYYY HH:mm');
          case 'time':
            return localMoment.format('HH:mm');
          case 'short':
            return localMoment.format('DD MMM');
          default:
            return localMoment.format('DD/MM/YYYY');
        }
      }

      const dateValue = typeof props.value === 'number'
        ? new Date(props.value)
        : props.value;

      return formatDate(dateValue, props.format, props.locale);
    });

    const fullDateTime = computed(() => {
      if (!props.value) return '';

      if (props.timezone) {
        const utcMoment = moment.utc(props.value);
        const localMoment = utcMoment.tz(props.timezone);
        return localMoment.format('DD/MM/YYYY HH:mm');
      }

      const dateValue = typeof props.value === 'number'
        ? new Date(props.value)
        : props.value;

      return formatDate(dateValue, 'datetime', props.locale);
    });

    const sizeClass = computed(() => `spm-date-display--${props.size}`);

    return {
      formattedDate,
      fullDateTime,
      sizeClass,
    };
  },
});
